import type { SearchCatalogItemsQuery } from "@albertine/types/openapi";
import { matchPath } from "react-router";
import { z } from "zod";
import type { CatalogQueryFilters } from "../common/CatalogItem";
import { fromCatalogQueryFiltersToUrlSearch } from "../common/types/OpenSearch.CatalogItem";
import { decodeSearchQuery, encodeSearchQuery } from "../common/utils/url.util";
import type { FirebaseId, MemberId } from "../common/validate";
import type { CatalogSearchUrlState } from "./customHook.util";

export const CatalogContext = z.enum([
    "service",
    "proactivity",
    "browse",
    "recommendations",
]);
export type CatalogContext = z.infer<typeof CatalogContext>;

const rootRoutes = {
    catalog: "/catalog" as const,
    themes: "/themes" as const,
    request: "/request" as const,
    member: "/member" as const,
};

export const routeSchemas = {
    catalog: `${rootRoutes.catalog}/:context/:mode/:catalogId?`,
    themes: rootRoutes.themes,
    themesMode: `${rootRoutes.themes}/:mode/:themeId?`,
    themesViewArticle: `${rootRoutes.themes}/:mode/:themeId/article/:articleId`,
    themesPreview: `${rootRoutes.themes}/preview`,
    request: `${rootRoutes.request}/:requestId`,
    requestBooking: `${rootRoutes.request}/:requestId/booking/:bookingId`,
    requestCatalog: `${rootRoutes.request}/:requestId/catalog/:context/:mode?/:catalogId?`,
    requestCatalogContext: (context: CatalogContext) =>
        `${rootRoutes.request}/:requestId/catalog/${context}/:mode?/:catalogId?`,
    requestWithMessage: `${rootRoutes.request}/:requestId/message/:messageId`,
    member: `${rootRoutes.member}/:memberId`,
    memberWithMessage: `${rootRoutes.member}/:memberId/message/:messageId`,
    memberCatalogContext: (context: CatalogContext) =>
        `${rootRoutes.member}/:memberId/catalog/${context}/:mode?/:catalogId?`,
    memberCatalog: `${rootRoutes.member}/:memberId/catalog/:context/:mode?/:catalogId?`,
};

const routes = {
    catalog: rootRoutes.catalog,

    catalogSearchInit: (
        context: CatalogContext,
        filter: SearchCatalogItemsQuery | undefined,
        locationSearchTerm: string | undefined,
        recommendedItemIds: string[] | undefined,
        requestId: string | undefined,
        memberId: string | undefined,
        viewAiRecommendations?: boolean,
    ) => {
        const filtersString = fromCatalogQueryFiltersToUrlSearch({
            filter,
            locationSearchTerm,
            recommendedItemIds,
            viewAiRecommendations,
        });

        if (requestId) {
            return `${rootRoutes.request}/${requestId}/catalog/${context}${
                filtersString ? `?${filtersString}` : ""
            }`;
        }

        if (memberId) {
            return `${rootRoutes.member}/${memberId}/catalog/${context}${
                filtersString ? `?${filtersString}` : ""
            }`;
        }

        return `${rootRoutes.catalog}/${context}${
            filtersString ? `?${filtersString}` : ""
        }`;
    },
    catalogSearch: (
        context: CatalogContext,
        requestId: string | undefined,
        memberId: string | undefined,
        item?: string,
    ) => {
        const searchParams = decodeSearchQuery(window.location.search);
        const newLocationSearch = item
            ? `?${encodeSearchQuery({
                  ...searchParams,
                  catalogId: item,
              })}`
            : window.location.search;

        if (requestId) {
            return `${rootRoutes.request}/${requestId}/catalog/${context}${newLocationSearch}`;
        }

        if (memberId) {
            return `${rootRoutes.member}/${memberId}/catalog/${context}${newLocationSearch}`;
        }

        return `${rootRoutes.catalog}/${context}/${newLocationSearch}`;
    },
    catalogEdit: (
        context: CatalogContext,
        item: string,
        requestId: string | undefined,
        memberId: string | undefined,
    ) => {
        if (requestId) {
            return `${rootRoutes.request}/${requestId}/catalog/${context}/edit/${item}${window.location.search}`;
        }

        if (memberId) {
            return `${rootRoutes.member}/${memberId}/catalog/${context}/edit/${item}${window.location.search}`;
        }

        return `${rootRoutes.catalog}/${context}/edit/${item}${window.location.search}`;
    },
    catalogView: (
        context: CatalogContext,
        item: string,
        requestId: string | undefined,
        memberId: string | undefined,
    ) => {
        if (requestId) {
            return `${rootRoutes.request}/${requestId}/catalog/${context}/view/${item}${window.location.search}`;
        }

        if (memberId) {
            return `${rootRoutes.member}/${memberId}/catalog/${context}/view/${item}${window.location.search}`;
        }

        return `${rootRoutes.catalog}/${context}/view/${item}${window.location.search}`;
    },
    catalogCreate: (
        context: CatalogContext,
        requestId: string | undefined,
        memberId: string | undefined,
    ) => {
        if (requestId) {
            return `${rootRoutes.request}/${requestId}/catalog/${context}/create${window.location.search}`;
        }

        if (memberId) {
            return `${rootRoutes.member}/${memberId}/catalog/${context}/create${window.location.search}`;
        }

        return `${rootRoutes.catalog}/${context}/create${window.location.search}`;
    },
    chatMessage: (
        memberId: string,
        messageId: string,
        requestId: string | undefined,
    ) =>
        requestId
            ? `/request/${requestId}/message/${messageId}`
            : `/member/${memberId}/message/${messageId}`,
    themes: () => `${rootRoutes.themes}${window.location.search}`,

    themesViewArticle: (themeId: string, articleId: string) =>
        `${rootRoutes.themes}/view/${themeId}/article/${articleId}${window.location.search}`,
    themesEditArticle: (themeId: string, articleId: string) =>
        `${rootRoutes.themes}/edit/${themeId}/article/${articleId}${window.location.search}`,

    themesEditItem: (themeId: string) =>
        `${rootRoutes.themes}/edit/${themeId}${window.location.search}`,
    themesCreateItem: () => `${rootRoutes.themes}/create${window.location.search}`,

    themesPreview: (memberId?: string) => {
        const searchParams = decodeSearchQuery(window.location.search);
        const newLocationSearch = memberId
            ? `?${encodeSearchQuery({
                  ...searchParams,
                  memberId,
              })}`
            : window.location.search;

        return `${rootRoutes.themes}/preview${newLocationSearch}`;
    },
    request: (requestId: string) => `${rootRoutes.request}/${requestId}`,
    requestBooking: (requestId: string, bookingId: string) =>
        `${rootRoutes.request}/${requestId}/booking/${bookingId}`,

    requestWithMessage: (requestId: FirebaseId, messageId: FirebaseId) =>
        `${rootRoutes.request}/${requestId}/message/${messageId}`,
    member: (memberId: MemberId) => `${rootRoutes.member}/${memberId}`,

    memberWithMessage: (memberId: MemberId, messageId: FirebaseId) =>
        `${rootRoutes.member}/${memberId}/message/${messageId}`,
};

export function findChatMessageIdFromPath(): string | undefined {
    const requestPathMessageId = (
        matchPath(window.location.pathname, {
            path: routeSchemas.requestWithMessage,
        })?.params as any
    )?.messageId;
    const memberPathMessageId = (
        matchPath(window.location.pathname, {
            path: routeSchemas.memberWithMessage,
        })?.params as any
    )?.messageId;

    return requestPathMessageId || memberPathMessageId;
}

export function findMemberIdFromPath(): string | undefined {
    const memberPathMemberId = (
        matchPath(window.location.pathname, {
            path: routeSchemas.member,
        })?.params as any
    )?.memberId;

    return memberPathMemberId;
}

export default routes;
